import { createRoot } from 'react-dom/client'

import { CacheProvider } from '@emotion/react'
import { LiveHelper } from '@liveHelper/components/LiveHelper'
import type { LiveHelperParams } from '@liveHelper/params'
import { EventType } from '@liveHelper/tracking/eventType'
import { TargetProvider } from '@oneToOne/shared/contexts/TargetContext'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import type { TW } from 'twind'

import ErrorBoundary from '@core/components/ErrorBoundary'
import { customEvent } from '@core/customEvent'
import { getSentryConfig, logError } from '@core/logging'
import { getLanguage } from '@core/params/helpers/getLanguage'
import { ensurePolyfills } from '@core/polyfills'
import { createEmbedEmotionCache } from '@core/styles/createEmbedEmotionCache'
import { TwindProvider } from '@core/twind'

interface AgentRenderProps {
  target: ShadowRoot
  widgetId: string
  widgetAttributes: Record<string, string>
  tw: TW
}

/**
 * A render function for rendering the agent widget.
 */
export function liveHelperRender({
  target,
  widgetId,
  widgetAttributes,
  tw,
}: AgentRenderProps) {
  const { chat_channel_id } = widgetAttributes
  const widget_id = widgetId

  const initialParams: LiveHelperParams = {
    type: 'live_helper',
    widget_id,
    lang: getLanguage(),
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  })

  ensurePolyfills(() => {
    const sentryConfig = getSentryConfig()
    sentryConfig.addTag('fwn_chat_channel_id', chat_channel_id ?? 'undefined')
    sentryConfig.addTag('fwn_context', 'liveHelper')

    try {
      if (target) {
        customEvent({
          type: EventType.FW_LIVE_HELPER_RENDER,
          data: {
            chat_channel_id,
            widget_id,
          },
          target,
        })

        const root = createRoot(target)
        root.unmount()
        root.render(
          <ErrorBoundary onError={(error) => logError(error)}>
            <TwindProvider tw={tw}>
              <CacheProvider
                value={createEmbedEmotionCache({
                  // Do not turn on speedy mode. It will break the rrweb snapshot.
                  speedy: false,
                  container: target,
                })}
              >
                <QueryClientProvider client={queryClient}>
                  <TargetProvider target={target}>
                    <LiveHelper
                      initialParams={initialParams}
                      widgetAttributes={widgetAttributes}
                    />
                  </TargetProvider>
                  <ReactQueryDevtools />
                </QueryClientProvider>
              </CacheProvider>
            </TwindProvider>
          </ErrorBoundary>,
        )
      }
    } catch (error) {
      logError(error)
    }
  })
}
